import { LogicDeps } from '@chegg-wt/core/state'
import { AppState, FSACreatorPayload } from '@chegg-wt/core/state/types'
import { createLogic } from 'redux-logic'
import { openModal } from '@chegg-wt/core/state/modals/actions'
import { REDESIGNED_UNLOCK_MODAL } from '@chegg-wt/core/state/modals/constants'
import {
  SESSION_STORAGE_KEYS,
  getSessionStorageItem,
  removeSessionStorageItem,
  setSessionStorageItem,
} from '@chegg-wt/core/utils/sessionStorage'
import { transformDeprecatedStyleId } from '@chegg-wt/core/utils/styles'
import { fillForm, formDataToJsonLegacy, submitForm } from '@chegg-wt/core/utils/form'

import { getCitationTitle, replaceCitationTitle } from '@chegg-wt/core/utils/citations'
import { CitationData } from '@chegg-wt/core/types/form'
import { routeParamsSelector } from '@chegg-wt/core/state/app/selectors'
import { FormType, FormTypes } from '@chegg-wt/core/constants/form'
import sourceTypes from '@chegg-wt/core/constants/sourceTypes'
import cloneDeep from 'lodash.clonedeep'
import {
  citationCreate,
  citationUpdate,
  citationValidated,
  citationUpdateSucceeded,
  citationCreateSucceeded,
} from '../citation/actions'
import { activeCitationSelector } from '../citation/selectors'
import { createCitationSourceTypeSelector } from '../createCitation/selectors'
import { CitationSuccessPayload } from '../citation/types'
import { flowed } from '../app/actions'
import { currentStyleSelector } from '../currentProject/selectors'
import { isProSelector } from '../user/selectors'
import {
  formFetchSucceeded,
  formFetchFailed,
  formLoaded,
  formTitleFetched,
  formSubmit,
  formPageAuthComplete,
} from './actions'
import {
  FILL_FORM,
  MANUAL_FORM_INITIALIZED,
  FORM_REQUESTED,
  FORM_SUBMIT_REQUESTED,
  FORM_SUBMIT_START,
  FORM_SUBMIT_STOP,
} from './constants'
import { FillFormMeta, FormRequestedMeta, FormPageAuthCompletePayload } from './types'

export const manualFormInitializedLogic = createLogic<
  AppState,
  { formType: FormType },
  any,
  LogicDeps
>({
  type: MANUAL_FORM_INITIALIZED,
  transform({ action }, next) {
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.searchData)
    next(action)
  },
})

export const setupFormRouteSessionLogic = createLogic<
  AppState,
  undefined,
  FormRequestedMeta,
  LogicDeps
>({
  type: FORM_REQUESTED,
  process({ action, getState }) {
    setSessionStorageItem(
      SESSION_STORAGE_KEYS.formRoute,
      JSON.stringify({
        formType: action.meta.formType,
        params: routeParamsSelector(getState()),
      })
    )
  },
})

export const formRequestedLogic = createLogic<
  AppState,
  undefined,
  FormRequestedMeta,
  LogicDeps
>({
  type: FORM_REQUESTED,
  async process({ action, api, getState }, dispatch, done) {
    const currentSourceType = createCitationSourceTypeSelector(getState()) || 'website'
    const style = currentStyleSelector(getState())
    const { formType } = action.meta
    const sourceTypeApiAlias = sourceTypes.find(
      (sourceType) => sourceType.id === currentSourceType
    ).apiAlias
    const citation = activeCitationSelector(getState())
    let title = getCitationTitle(citation)
    try {
      if (title && formType === FormTypes.NEW) {
        ;[title] = await api.bff.capitalizeCitations({
          citationsInput: [title],
        })
      }

      dispatch(formTitleFetched(title))
      const { form } = await api.getForm(sourceTypeApiAlias, style)
      dispatch(formFetchSucceeded(form))
    } catch (e) {
      // handle error
      dispatch(formFetchFailed(e))
    }

    done()
  },
})

export const formLoadedLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof formLoaded>,
  any,
  LogicDeps
>({
  type: [formLoaded],
  transform({ action }, next) {
    action.payload.style = transformDeprecatedStyleId(action.payload.style) // eslint-disable-line no-param-reassign
    next(action)
  },
})

export const fillFormLogic = createLogic<
  AppState,
  { sessionFormData?: CitationData },
  FillFormMeta,
  LogicDeps
>({
  type: FILL_FORM,
  transform({ action: originalAction }, next) {
    try {
      const sessionFormData: CitationData = JSON.parse(
        getSessionStorageItem(SESSION_STORAGE_KEYS.formData)
      )

      next({
        ...originalAction,
        payload: {
          sessionFormData,
        },
      })
    } catch (error) {
      next(originalAction)
    }
  },
  async process({ action, getState }, dispatch, done) {
    const { sessionFormData } = action.payload
    const {
      navigateFn,
      onUserClickAddAnnotationCTA,
      title,
      onUserClickAddContributorsCTA,
      theme,
      isSubmitting,
    } = action.meta

    const style = currentStyleSelector(getState())
    const citation = activeCitationSelector(getState())
    const handleChangeSourceType = (sourceType) => navigateFn(style)(sourceType.id)
    const updatedCitation =
      sessionFormData || replaceCitationTitle(cloneDeep(citation), title)
    fillForm({
      style,
      citation: updatedCitation,
      theme,
      onChangeSourceType: handleChangeSourceType,
      onUserClickAddAnnotationCTA,
      onUserClickAddContributorsCTA,
    })

    removeSessionStorageItem(SESSION_STORAGE_KEYS.formData)

    const isProUser = isProSelector(getState())
    if (sessionFormData && isProUser && !isSubmitting) {
      dispatch(formSubmit())
    }

    done()
  },
})

export const setupFormDataSessionLogic = createLogic<AppState, undefined, any, LogicDeps>(
  {
    type: [FORM_SUBMIT_START],
    process() {
      setSessionStorageItem(
        SESSION_STORAGE_KEYS.formData,
        JSON.stringify(formDataToJsonLegacy())
      )
    },
  }
)

export const formSubmitLogic = createLogic<AppState, {}, any, LogicDeps>({
  type: [FORM_SUBMIT_REQUESTED],
  process(_, dispatch, done) {
    submitForm((citationData, initialCitationData, annotationText) => {
      dispatch(citationValidated(citationData.validatorStatus))

      const isNewCitation = !initialCitationData.citationId

      const { validatorStatus, ...restCitationData } = citationData
      if (isNewCitation) {
        dispatch(citationCreate(restCitationData, annotationText))
      } else {
        dispatch(citationUpdate(initialCitationData, restCitationData, annotationText))
      }

      done()
    })
  },
})

export const teardownFormDataSessionLogic = createLogic<AppState, {}, any, LogicDeps>({
  type: [citationCreateSucceeded, citationUpdateSucceeded, FORM_SUBMIT_STOP],
  process() {
    removeSessionStorageItem(SESSION_STORAGE_KEYS.formData)
  },
})

export const teardownFormRouteSessionLogic = createLogic<AppState, {}, any, LogicDeps>({
  type: [citationCreateSucceeded, citationUpdateSucceeded],
  process() {
    removeSessionStorageItem(SESSION_STORAGE_KEYS.formRoute)
  },
})

export const successfulFormSubmitLogic = createLogic<
  AppState,
  CitationSuccessPayload,
  any,
  LogicDeps
>({
  type: [citationCreateSucceeded, citationUpdateSucceeded],
  validate({ action }, allow, reject) {
    if (!action.payload.navigate) {
      reject(action)
    }
    allow(action)
  },
  process() {
    return flowed('toBibliography')
  },
})

export const formPageAuthCompleteLogic = createLogic<
  AppState,
  FormPageAuthCompletePayload,
  any,
  LogicDeps
>({
  type: [formPageAuthComplete],
  process({ action, getState }, dispatch, done) {
    const isPro = isProSelector(getState())

    const { alwaysParticipate } = action.payload

    if (isPro) {
      dispatch(formSubmit())
    } else {
      dispatch(
        openModal({
          name: REDESIGNED_UNLOCK_MODAL,
          data: { withAuthBanner: false, alwaysParticipate },
        })
      )
    }
    done()
  },
})
